import React from 'react';
import { useNavigate } from 'react-router-dom';
import './AISolutionsPage.css';

const AISolutionsPage = () => {
  const navigate = useNavigate();

  const services = [
    {
      title: 'Custom AI Applications',
      icon: '🤖',
      description: 'Tailored AI solutions designed to solve your unique business challenges.',
      features: [
        'AI-powered chatbots and assistants',
        'Intelligent document processing',
        'Recommendation systems',
        'Computer vision applications',
        'Natural language processing',
        'Custom AI model development'
      ]
    },
    {
      title: 'Fine Tuning of LLMs',
      icon: '🎯',
      description: 'Optimize large language models for your specific domain and use cases.',
      features: [
        'Domain-specific model training',
        'Custom prompt engineering',
        'Model performance optimization',
        'Instruction tuning',
        'Retrieval augmented generation (RAG)',
        'Model evaluation and benchmarking'
      ]
    },
    {
      title: 'ML Model Development',
      icon: '📈',
      description: 'End-to-end machine learning solutions from concept to production.',
      features: [
        'Predictive analytics models',
        'Classification and regression',
        'Time series forecasting',
        'Anomaly detection',
        'Clustering and segmentation',
        'Model deployment and monitoring'
      ]
    },
    {
      title: 'AI Integration',
      icon: '🔗',
      description: 'Seamlessly integrate AI capabilities into your existing systems.',
      features: [
        'API development and integration',
        'Cloud AI services setup',
        'Workflow automation with AI',
        'Legacy system modernization',
        'Multi-model orchestration',
        'Performance optimization'
      ]
    }
  ];

  const technologies = [
    { name: 'OpenAI GPT', category: 'LLMs' },
    { name: 'Anthropic Claude', category: 'LLMs' },
    { name: 'Llama', category: 'Open Source' },
    { name: 'TensorFlow', category: 'Framework' },
    { name: 'PyTorch', category: 'Framework' },
    { name: 'LangChain', category: 'Development' },
    { name: 'Hugging Face', category: 'Platform' },
    { name: 'Vertex AI', category: 'Cloud' },
    { name: 'Azure OpenAI', category: 'Cloud' },
    { name: 'AWS Bedrock', category: 'Cloud' },
    { name: 'Vector DBs', category: 'Infrastructure' },
    { name: 'MLflow', category: 'MLOps' }
  ];

  const benefits = [
    {
      title: 'Automation at Scale',
      description: 'Automate repetitive tasks and scale operations efficiently',
      icon: '⚡'
    },
    {
      title: 'Enhanced Accuracy',
      description: 'Reduce errors with intelligent prediction and validation',
      icon: '🎯'
    },
    {
      title: 'Competitive Edge',
      description: 'Stay ahead with cutting-edge AI capabilities',
      icon: '🚀'
    },
    {
      title: 'Cost Savings',
      description: 'Reduce operational costs through intelligent automation',
      icon: '💰'
    }
  ];

  const useCases = [
    {
      title: 'Intelligent Automation',
      description: 'Automate complex business processes with AI',
      icon: '⚙️',
      examples: [
        'Document processing and extraction',
        'Email classification and routing',
        'Invoice and receipt processing',
        'Contract analysis and review',
        'Customer support automation',
        'Data entry automation'
      ]
    },
    {
      title: 'Conversational AI',
      description: 'Build intelligent chat and voice interfaces',
      icon: '💬',
      examples: [
        'Customer service chatbots',
        'Virtual sales assistants',
        'Internal knowledge assistants',
        'Voice-enabled applications',
        'Multi-language support',
        'Sentiment analysis'
      ]
    },
    {
      title: 'Predictive Analytics',
      description: 'Forecast trends and make data-driven decisions',
      icon: '🔮',
      examples: [
        'Sales forecasting',
        'Demand prediction',
        'Churn prevention',
        'Risk assessment',
        'Inventory optimization',
        'Price optimization'
      ]
    },
    {
      title: 'Computer Vision',
      description: 'Extract insights from images and videos',
      icon: '👁️',
      examples: [
        'Quality control inspection',
        'Object detection and tracking',
        'Facial recognition',
        'OCR and document scanning',
        'Image classification',
        'Video analytics'
      ]
    }
  ];

  const process = [
    {
      step: '01',
      title: 'Discovery & Feasibility',
      description: 'Assess your AI readiness, identify opportunities, and validate use cases'
    },
    {
      step: '02',
      title: 'Data Preparation',
      description: 'Collect, clean, and prepare high-quality training data'
    },
    {
      step: '03',
      title: 'Model Development',
      description: 'Build, train, and fine-tune AI models for optimal performance'
    },
    {
      step: '04',
      title: 'Testing & Validation',
      description: 'Rigorous testing to ensure accuracy, reliability, and ethics'
    },
    {
      step: '05',
      title: 'Deployment & Monitoring',
      description: 'Production deployment with continuous monitoring and improvement'
    }
  ];

  const aiCapabilities = [
    {
      category: 'Natural Language Processing',
      capabilities: [
        'Text generation and summarization',
        'Named entity recognition',
        'Sentiment analysis',
        'Language translation',
        'Question answering systems',
        'Content moderation'
      ]
    },
    {
      category: 'Machine Learning',
      capabilities: [
        'Supervised learning models',
        'Unsupervised clustering',
        'Reinforcement learning',
        'Deep learning networks',
        'Ensemble methods',
        'AutoML solutions'
      ]
    },
    {
      category: 'Computer Vision',
      capabilities: [
        'Image classification',
        'Object detection',
        'Semantic segmentation',
        'Face recognition',
        'OCR and text extraction',
        'Video analysis'
      ]
    }
  ];

  const industries = [
    { name: 'Healthcare', icon: '🏥', use: 'Diagnosis assistance, patient monitoring' },
    { name: 'Finance', icon: '💳', use: 'Fraud detection, risk assessment' },
    { name: 'Retail', icon: '🛍️', use: 'Personalization, inventory optimization' },
    { name: 'Manufacturing', icon: '🏭', use: 'Quality control, predictive maintenance' },
    { name: 'Legal', icon: '⚖️', use: 'Contract analysis, legal research' },
    { name: 'Education', icon: '📚', use: 'Personalized learning, assessment' }
  ];

  return (
    <div className="ai-solutions-page">
      {/* Hero Section */}
      <section className="ai-hero">
        <div className="container">
          <button className="back-btn" onClick={() => navigate('/')}>
            ← Back to Home
          </button>
          <div className="hero-content">
            <h1 className="hero-title">
              AI Solutions & Innovation
            </h1>
            <p className="hero-subtitle">
              Harness the power of artificial intelligence to transform your business with cutting-edge solutions that drive efficiency and innovation
            </p>
            <div className="hero-stats">
              <div className="stat">
                <h3>50+</h3>
                <p>AI Models Deployed</p>
              </div>
              <div className="stat">
                <h3>85%</h3>
                <p>Automation Rate</p>
              </div>
              <div className="stat">
                <h3>10x</h3>
                <p>Faster Processing</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="services-detail">
        <div className="container">
          <h2 className="section-title">Our AI Services</h2>
          <p className="section-subtitle">
            Comprehensive AI solutions tailored to your business needs
          </p>
          <div className="services-grid">
            {services.map((service, index) => (
              <div key={index} className="service-detail-card">
                <div className="service-icon-large">{service.icon}</div>
                <h3>{service.title}</h3>
                <p className="service-desc">{service.description}</p>
                <ul className="features-list">
                  {service.features.map((feature, idx) => (
                    <li key={idx}>
                      <span className="checkmark">✓</span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technologies */}
      <section className="technologies">
        <div className="container">
          <h2 className="section-title">AI Technologies We Work With</h2>
          <p className="section-subtitle">
            Leading-edge AI tools and frameworks
          </p>
          <div className="tech-grid">
            {technologies.map((tech, index) => (
              <div key={index} className="tech-item">
                <span className="tech-name">{tech.name}</span>
                <span className="tech-category">{tech.category}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="use-cases">
        <div className="container">
          <h2 className="section-title">AI Use Cases</h2>
          <p className="section-subtitle">
            Real-world applications transforming businesses
          </p>
          <div className="use-cases-grid">
            {useCases.map((useCase, index) => (
              <div key={index} className="use-case-card">
                <div className="use-case-icon">{useCase.icon}</div>
                <h3>{useCase.title}</h3>
                <p className="use-case-desc">{useCase.description}</p>
                <ul className="examples-list">
                  {useCase.examples.map((example, idx) => (
                    <li key={idx}>{example}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI Capabilities */}
      <section className="ai-capabilities">
        <div className="container">
          <h2 className="section-title">Core AI Capabilities</h2>
          <p className="section-subtitle">
            Advanced AI techniques at your service
          </p>
          <div className="capabilities-grid">
            {aiCapabilities.map((category, index) => (
              <div key={index} className="capability-category">
                <h3>{category.category}</h3>
                <ul className="capability-list">
                  {category.capabilities.map((capability, idx) => (
                    <li key={idx}>
                      <span className="bullet">●</span>
                      {capability}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="industries">
        <div className="container">
          <h2 className="section-title">Industries We Serve</h2>
          <p className="section-subtitle">
            AI solutions across diverse sectors
          </p>
          <div className="industries-grid">
            {industries.map((industry, index) => (
              <div key={index} className="industry-card">
                <div className="industry-icon">{industry.icon}</div>
                <h3>{industry.name}</h3>
                <p>{industry.use}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="benefits">
        <div className="container">
          <h2 className="section-title">Why Choose Our AI Solutions</h2>
          <div className="benefits-grid">
            {benefits.map((benefit, index) => (
              <div key={index} className="benefit-card">
                <div className="benefit-icon">{benefit.icon}</div>
                <h3>{benefit.title}</h3>
                <p>{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="process">
        <div className="container">
          <h2 className="section-title">Our AI Development Process</h2>
          <p className="section-subtitle">
            From concept to production-ready AI solutions
          </p>
          <div className="process-timeline">
            {process.map((item, index) => (
              <div key={index} className="process-step">
                <div className="step-number">{item.step}</div>
                <div className="step-content">
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Embrace AI Innovation?</h2>
            <p>Let's explore how AI can revolutionize your business operations and outcomes</p>
            <div className="cta-buttons">
              <button className="primary-btn" onClick={() => navigate('/#contact')}>
                Start Your AI Journey
              </button>
              <button className="secondary-btn" onClick={() => navigate('/')}>
                Explore More Services
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AISolutionsPage;
